// src/components/students/AddStudent.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStudents } from './StudentProvider';
import { 
  ArrowLeft, 
  Save, 
  X, 
  Calendar,
  User,
  Mail,
  Phone,
  Target,
  FileText,
  AlertCircle,
  Users,
  Heart,
  BookOpen,
  Tag,
  Camera,
  Upload,
  Image as ImageIcon
} from 'lucide-react';

// Reusable form components
const InputField = ({ label, name, type = 'text', required = false, placeholder, icon: Icon, value, onChange, errors, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      {Icon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-gray-400 dark:text-gray-400" />
        </div>
      )}
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full ${Icon ? 'pl-10' : 'pl-3'} pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
          errors[name] ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-gray-600'
        }`}
        {...props}
      />
    </div>
    {errors[name] && (
      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors[name]}</p>
    )}
  </div>
);

const SelectField = ({ label, name, options, required = false, value, onChange, errors }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
        errors[name] ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-gray-600'
      }`}
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    {errors[name] && (
      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors[name]}</p>
    )}
  </div>
);

const TextAreaField = ({ label, name, required = false, placeholder, rows = 3, value, onChange, errors }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
        errors[name] ? 'border-red-300 dark:border-red-500' : 'border-gray-200 dark:border-gray-600'
      }`}
    />
    {errors[name] && (
      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors[name]}</p>
    )}
  </div>
);

const AddStudent = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get student ID from URL for edit mode
  const { students, getStudent, addStudent, updateStudent, loading } = useStudents();
  const photoInputRef = useRef(null);
  
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState({
    studentFirstName: '',
    studentLastName: '',
    dateOfBirth: '',
    studentAge: '',
    gender: '',
    experience: '',
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    notes: '',
    emergencyContact: '',
    medicalNotes: '',
    interests: [],
    photoUrl: '',
    photoFile: null
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentInterest, setCurrentInterest] = useState('');

  // Load existing student data in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const loadStudent = async () => {
        try {
          console.log('AddStudent - Looking for student with ID:', id);
          console.log('AddStudent - Available students:', students);
          
          // Find student in the loaded students array
          let studentData = students.find(s => {
            return s.id === id || s.id === parseInt(id) || String(s.id) === String(id);
          });
          
          if (!studentData) {
            // Try the getStudent function
            studentData = getStudent(id);
          }
          
          if (studentData) {
            console.log('AddStudent - Found student:', studentData);
            // Format date_of_birth to YYYY-MM-DD for date input
            let formattedDateOfBirth = '';
            const dob = studentData.dateOfBirth || studentData.date_of_birth;
            if (dob) {
              const date = new Date(dob);
              formattedDateOfBirth = date.toISOString().split('T')[0];
            }

            setFormData({
              studentFirstName: studentData.studentFirstName || studentData.student_first_name || studentData.firstName || '',
              studentLastName: studentData.studentLastName || studentData.student_last_name || studentData.lastName || '',
              dateOfBirth: formattedDateOfBirth,
              studentAge: studentData.studentAge || studentData.student_age || studentData.age || '',
              gender: studentData.gender || '',
              experience: studentData.experience || '',
              parentName: studentData.parentName || studentData.parent_name || '',
              parentEmail: studentData.parentEmail || studentData.parent_email || '',
              parentPhone: studentData.parentPhone || studentData.parent_phone || '',
              notes: studentData.notes || '',
              emergencyContact: studentData.emergencyContact || studentData.emergency_contact || '',
              medicalNotes: studentData.medicalNotes || studentData.medical_notes || '',
              interests: studentData.interests || [],
              photoUrl: studentData.photoUrl || studentData.photo_url || '',
              photoFile: null
            });
          } else {
            console.log('AddStudent - Student not found');
            setSubmitError('Student not found');
          }
        } catch (error) {
          console.error('Failed to load student:', error);
          setSubmitError('Failed to load student data. Please try again.');
        }
      };
      loadStudent();
    }
  }, [isEditMode, id, students, getStudent]);

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limitedPhoneNumber = phoneNumber.slice(0, 10);
    
    // Apply formatting based on length
    if (limitedPhoneNumber.length <= 3) {
      return limitedPhoneNumber;
    } else if (limitedPhoneNumber.length <= 6) {
      return `(${limitedPhoneNumber.slice(0, 3)}) ${limitedPhoneNumber.slice(3)}`;
    } else {
      return `(${limitedPhoneNumber.slice(0, 3)}) ${limitedPhoneNumber.slice(3, 6)}-${limitedPhoneNumber.slice(6)}`;
    }
  };

  // Validate phone number format
  const isValidPhoneNumber = (phone) => {
    // Remove all non-digit characters and check if we have 10 digits
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10;
  };

  // Handle form field changes
  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';

    const today = new Date();
    const birthDate = new Date(dateOfBirth);

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    // Adjust age if birthday hasn't occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= 0 ? age : '';
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    let processedValue = value;

    // Special handling for phone number fields
    if (name === 'parentPhone') {
      processedValue = formatPhoneNumber(value);
    } else if (type === 'number') {
      processedValue = parseInt(value) || '';
    }

    // Calculate age when date of birth changes
    if (name === 'dateOfBirth') {
      const age = calculateAge(value);
      setFormData(prev => ({
        ...prev,
        dateOfBirth: value,
        studentAge: age
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: processedValue
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle interest addition
  const addInterest = () => {
    if (currentInterest.trim() && !formData.interests.includes(currentInterest.trim())) {
      setFormData(prev => ({
        ...prev,
        interests: [...prev.interests, currentInterest.trim()]
      }));
      setCurrentInterest('');
    }
  };

  // Handle interest removal
  const removeInterest = (interestToRemove) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.filter(interest => interest !== interestToRemove)
    }));
  };

  // Handle interest input key press
  const handleInterestKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addInterest();
    }
  };

  // Handle photo upload
  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setSubmitError('Photo file size must be less than 5MB');
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        setSubmitError('Please select a valid image file');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          photoUrl: reader.result,
          photoFile: file
        }));
      };
      reader.readAsDataURL(file);
      setSubmitError('');
    }
  };

  // Remove photo
  const removePhoto = () => {
    setFormData(prev => ({
      ...prev,
      photoUrl: '',
      photoFile: null
    }));
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.studentFirstName.trim()) {
      newErrors.studentFirstName = 'First name is required';
    }

    if (!formData.studentLastName.trim()) {
      newErrors.studentLastName = 'Last name is required';
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const age = calculateAge(formData.dateOfBirth);
      if (age < 3 || age > 100) {
        newErrors.dateOfBirth = 'Student must be between 3 and 100 years old';
      }
    }

    // Only validate parent info if student is a minor (under 18)
    const studentIsMinor = formData.dateOfBirth && calculateAge(formData.dateOfBirth) < 18;

    if (studentIsMinor) {
      if (!formData.parentName.trim()) {
        newErrors.parentName = 'Parent/Guardian name is required';
      }

      if (!formData.parentEmail.trim()) {
        newErrors.parentEmail = 'Parent email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.parentEmail)) {
        newErrors.parentEmail = 'Please enter a valid email address';
      }

      if (!formData.parentPhone.trim()) {
        newErrors.parentPhone = 'Parent phone number is required';
      } else if (!isValidPhoneNumber(formData.parentPhone)) {
        newErrors.parentPhone = 'Please enter a valid 10-digit phone number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      let photoUrl = formData.photoUrl;

      // Upload photo if a new file was selected
      if (formData.photoFile) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', formData.photoFile);

        const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
        const uploadResponse = await fetch(`${API_BASE_URL}/upload/single`, {
          method: 'POST',
          body: uploadFormData,
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload photo');
        }

        const uploadResult = await uploadResponse.json();
        photoUrl = uploadResult.file.url;
      }

      // Prepare student data with uploaded photo URL
      const studentData = {
        ...formData,
        photoUrl: photoUrl,
        photoFile: undefined // Remove file object before sending
      };

      if (isEditMode) {
        console.log('Updating student:', id, studentData);
        await updateStudent(id, studentData);
      } else {
        console.log('Creating student:', studentData);
        await addStudent(studentData);
      }
      
      navigate('/students');
    } catch (error) {
      console.error('Failed to save student:', error);
      setSubmitError(error.message || 'Failed to save student. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (confirm('Are you sure you want to cancel? Your changes will be lost.')) {
      navigate('/students');
    }
  };

  // Determine if student is a minor (under 18) based on date of birth
  const isMinor = formData.dateOfBirth && calculateAge(formData.dateOfBirth) < 18;

  // Check if parent data exists (for imported students without age data)
  const hasParentData = formData.parentName || formData.parentEmail || formData.parentPhone;

  // Show parent fields if student is a minor OR if parent data already exists
  const showParentFields = isMinor || hasParentData;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/students')}
              className="p-2 text-gray-400 dark:text-gray-400 hover:text-[#7A99D8] rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Users className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {isEditMode ? 'Edit Student' : 'Add New Student'}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {isEditMode ? 'Update student information' : 'Add a new student to your roster'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div>{/* Content wrapper */}

        {/* Error Message */}
        {submitError && (
          <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-700 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error {isEditMode ? 'updating' : 'creating'} student</h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{submitError}</p>
            </div>
            <button
              onClick={() => setSubmitError('')}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Form */}
        <form id="student-form" onSubmit={handleSubmit} className="space-y-8">
          {/* Student Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
              <User className="w-5 h-5" />
              Student Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InputField
                label="First Name"
                name="studentFirstName"
                required
                placeholder="e.g., Emma"
                icon={User}
                value={formData.studentFirstName}
                onChange={handleChange}
                errors={errors}
              />
              
              <InputField
                label="Last Name"
                name="studentLastName"
                required
                placeholder="e.g., Johnson"
                icon={User}
                value={formData.studentLastName}
                onChange={handleChange}
                errors={errors}
              />
              
              <InputField
                label="Date of Birth"
                name="dateOfBirth"
                type="date"
                required
                placeholder="Select date"
                icon={Calendar}
                value={formData.dateOfBirth}
                onChange={handleChange}
                errors={errors}
              />

              {formData.dateOfBirth && formData.studentAge !== '' && (
                <div className="px-4 py-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <span className="font-medium">Age:</span> {formData.studentAge} years old
                  </p>
                </div>
              )}
              
              <SelectField
                label="Gender"
                name="gender"
                options={[
                  { value: '', label: 'Select Gender' },
                  { value: 'Male', label: 'Male' },
                  { value: 'Female', label: 'Female' }
                ]}
                value={formData.gender}
                onChange={handleChange}
                errors={errors}
              />
              
              <SelectField
                label="Experience Level"
                name="experience"
                required
                options={[
                  { value: '', label: 'Select Experience Level' },
                  { value: 'Beginner', label: 'Beginner' },
                  { value: 'Intermediate', label: 'Intermediate' },
                  { value: 'Advanced', label: 'Advanced' }
                ]}
                value={formData.experience}
                onChange={handleChange}
                errors={errors}
              />
            </div>
          </div>

          {/* Parent/Guardian Information - Show for minors (under 18) or if parent data exists */}
          {showParentFields && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Parent/Guardian Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <InputField
                    label="Parent/Guardian Name"
                    name="parentName"
                    required={isMinor}
                    placeholder="e.g., Sarah Johnson"
                    icon={User}
                    value={formData.parentName}
                    onChange={handleChange}
                    errors={errors}
                  />
                </div>

                <InputField
                  label="Email Address"
                  name="parentEmail"
                  type="email"
                  required={isMinor}
                  placeholder="e.g., sarah.johnson@email.com"
                  icon={Mail}
                  value={formData.parentEmail}
                  onChange={handleChange}
                  errors={errors}
                />

                <InputField
                  label="Phone Number"
                  name="parentPhone"
                  type="tel"
                  required={isMinor}
                  placeholder="(555) 123-4567"
                  icon={Phone}
                  value={formData.parentPhone}
                  onChange={handleChange}
                  errors={errors}
                  maxLength="14"
                  autoComplete="tel"
                />
              </div>
            </div>
          )}

          {/* Emergency Contact & Medical Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
              <Heart className="w-5 h-5" />
              Emergency Contact & Medical Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Emergency Contact"
                name="emergencyContact"
                placeholder="e.g., Mark Johnson (555) 123-4568"
                icon={Phone}
                value={formData.emergencyContact}
                onChange={handleChange}
                errors={errors}
              />
              
              <TextAreaField
                label="Medical Notes"
                name="medicalNotes"
                placeholder="Any allergies, medical conditions, or special requirements..."
                rows={3}
                value={formData.medicalNotes}
                onChange={handleChange}
                errors={errors}
              />
            </div>
          </div>

          {/* Student Photo */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Student Photo
            </h2>
            <div className="space-y-4">
              {formData.photoUrl ? (
                <div className="relative inline-block">
                  <img 
                    src={formData.photoUrl} 
                    alt="Student photo"
                    className="w-32 h-32 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                  />
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="w-32 h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <ImageIcon className="w-8 h-8 text-gray-400 dark:text-gray-400 mx-auto mb-2" />
                    <span className="text-sm text-gray-500 dark:text-gray-300">No photo</span>
                  </div>
                </div>
              )}
              
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-blue-600 dark:hover:bg-blue-700 text-gray-900 dark:text-gray-100 rounded-lg transition-colors font-medium"
                >
                  <Upload className="w-4 h-4" />
                  {formData.photoUrl ? 'Change Photo' : 'Upload Photo'}
                </button>
                {formData.photoUrl && (
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
                  >
                    <X className="w-4 h-4" />
                    Remove
                  </button>
                )}
              </div>
              
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              
              <p className="text-xs text-gray-500 dark:text-gray-300">
                Upload a photo of the student. Maximum file size: 5MB. Supported formats: JPG, PNG, GIF.
              </p>
            </div>
          </div>

          {/* Additional Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Additional Information
            </h2>
            <div className="space-y-6">
              <TextAreaField
                label="Notes"
                name="notes"
                placeholder="Any additional information about the student..."
                rows={4}
                value={formData.notes}
                onChange={handleChange}
                errors={errors}
              />
              
              {/* Interests */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Interests & Hobbies
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.interests.map((interest, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm rounded-full"
                    >
                      {interest}
                      <button
                        type="button"
                        onClick={() => removeInterest(interest)}
                        className="ml-1 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={currentInterest}
                    onChange={(e) => setCurrentInterest(e.target.value)}
                    onKeyPress={handleInterestKeyPress}
                    placeholder="Add an interest..."
                    className="flex-1 border border-gray-200 dark:border-gray-600 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#7A99D8] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  <button
                    type="button"
                    onClick={addInterest}
                    className="px-4 py-2 bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-blue-600 dark:hover:bg-blue-700 text-gray-900 dark:text-gray-100 rounded-lg flex items-center gap-1 font-medium"
                  >
                    <Tag className="w-4 h-4" />
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Spacer for sticky buttons */}
          <div className="h-20"></div>
        </form>
        </div>

        {/* Sticky Form Actions */}
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 shadow-lg z-10">
          <div className="max-w-4xl mx-auto flex items-center justify-end gap-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="student-form"
              disabled={submitting || loading}
              className="px-6 py-2 bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-blue-600 dark:hover:bg-blue-700 text-gray-900 dark:text-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-medium"
            >
              {submitting || loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 dark:border-gray-100"></div>
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditMode ? 'Update Student' : 'Create Student'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddStudent;